import type { ButtonProps } from '@material-ui/core/Button';
import MuiButton from '@material-ui/core/Button';
import classNames from 'clsx';
import type { MouseEvent } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';

import Loader from '@swf/components/StarsButton/components/Loader/Loader';
import Translation from '@swf/components/Translation/Translation';
import Arrow from '@swf/core/assets/icons/arrow.svg';
import type { RoutingKey } from '@swf/core/enums';
import useNavigate from '@swf/core/hooks/useNavigate';
import useTrackers from '@swf/core/hooks/useTrackers';
import useTrackingLabel from '@swf/core/hooks/useTrackingLabel';
import { select } from '@swf/core/store';
import type { ClassNameProp } from '@swf/core/types/Props';
import type { TrackedTranslation, Values } from '@swf/core/types/i18n';
import { generatePath } from '@swf/core/utils/paths';

import styles from './Button.scss';

export interface Props extends Omit<ButtonProps, 'variant'>, Partial<ClassNameProp> {
  align?: 'left' | 'right';
  route?: RoutingKey;
  translation?: TrackedTranslation;
  trackingValue?: number;
  values?: Values;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'dropdown' | 'help';
  selected?: boolean;
  loading?: boolean;
}

const Button: React.FC<Props> = ({
  translation,
  trackingValue: value,
  values,
  variant = 'primary',
  size,
  className,
  onClick,
  children,
  selected,
  align,
  route,
  loading,
  ...rest
}) => {
  const { buttonPressed } = useTrackers();
  const buttonName = useTrackingLabel(translation, values);
  const navigate = useNavigate();
  const { routes } = useSelector(select.page.getMeta);

  const isLink = ['text', 'help', 'dropdown'].includes(variant);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    buttonPressed({ buttonName, value });
    route ? routes && navigate(generatePath(routes[route], {})) : onClick?.(event);
  };

  return (
    <MuiButton
      onClick={handleClick}
      disableRipple={isLink}
      size={isLink ? 'small' : 'large'}
      classes={{ endIcon: classNames({ [styles.endIcon]: variant === 'help' }) }}
      className={classNames(
        styles[variant as string],
        styles[size as string],
        styles[align as string],
        styles.root,
        styles.label,
        className,
        {
          [styles.loading]: loading,
          [styles.selected]: selected
        }
      )}
      endIcon={variant === 'help' && <Arrow className={styles.arrow} />}
      {...rest}
    >
      {(loading && <Loader />) ||
        (translation && !children && <Translation {...translation} values={values} />) ||
        children}
    </MuiButton>
  );
};

export default Button;
